import type { GraphNodeAnchor } from '../../entities/graph-document';

export interface DepthEstimationOptions {
	forceReestimateAll?: boolean;
	nodeIdsNeedingDepth?: string[];
}

export function estimateNodeAnchorDepths(
	nodeAnchors: Record<string, GraphNodeAnchor>,
	options: DepthEstimationOptions = {}
): Record<string, GraphNodeAnchor> {
	const entries = Object.entries(nodeAnchors);
	if (entries.length === 0) {
		return nodeAnchors;
	}

	const idsNeedingDepth = new Set(
		options.forceReestimateAll ? entries.map(([nodeId]) => nodeId) : options.nodeIdsNeedingDepth ?? []
	);
	if (idsNeedingDepth.size === 0) {
		return { ...nodeAnchors };
	}

	const centroid = entries.reduce(
		(accumulator, [, anchor]) => ({
			x: accumulator.x + anchor.x,
			y: accumulator.y + anchor.y,
		}),
		{ x: 0, y: 0 }
	);
	const centerX = centroid.x / entries.length;
	const centerY = centroid.y / entries.length;
	const enriched = entries.map(([nodeId, anchor], index) => {
		const deltaX = anchor.x - centerX;
		const deltaY = anchor.y - centerY;

		return {
			nodeId,
			anchor,
			index,
			radius: Math.hypot(deltaX, deltaY),
			angle: Math.atan2(deltaY, deltaX),
		};
	});
	const maximumRadius = Math.max(...enriched.map((entry) => entry.radius), 0);
	const ordered = [...enriched].sort((left, right) =>
		left.radius - right.radius || left.angle - right.angle || left.index - right.index
	);
	const estimatedDepthByNodeId = new Map<string, number>();

	ordered.forEach((entry, index) => {
		if (!idsNeedingDepth.has(entry.nodeId)) {
			return;
		}

		let z = 0;
		if (maximumRadius === 0) {
			z = (index - (ordered.length - 1) / 2) * 48;
		} else {
			const radialBand = Math.round((entry.radius / maximumRadius) * Math.min(4, Math.max(1, ordered.length - 1)));
			const sector = Math.floor(((entry.angle + Math.PI) / (Math.PI * 2)) * 6);
			z = (radialBand - 2) * 72 + (sector % 2 === 0 ? 24 : -24) + ((index % 3) - 1) * 18;
		}

		estimatedDepthByNodeId.set(entry.nodeId, Math.round(z));
	});

	return Object.fromEntries(
		entries.map(([nodeId, anchor]) => [
			nodeId,
			{
				...anchor,
				z: estimatedDepthByNodeId.get(nodeId) ?? anchor.z,
			},
		])
	);
}